import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SourceStatusEnum } from '@am3nd-org/pointcraft-types';

import { storeEvents, STORE_EVENTS } from '../utils';
import type { Customer, Transaction, CustomerFilters } from '../types';

/**
 * Helper to check if a status represents a successful/paid transaction
 */
const isCompletedStatus = (status: SourceStatusEnum): boolean =>
  status === SourceStatusEnum.PAID || status === SourceStatusEnum.OVERPAID;

/* =================== CUSTOMER STORE STATE =================== */
interface CustomerState {
  customers: Customer[];
  isLoading: boolean;
  error: string | null;

  // CRUD OPERATIONS
  getCustomers: () => Customer[];
  getCustomerById: (id: string) => Customer | undefined;
  createCustomer: (customer: Omit<Customer, 'id' | 'joinedDate'>) => Customer;
  updateCustomer: (id: string, updates: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;

  // FILTERING & SEARCHING
  filterCustomers: (filters: CustomerFilters) => Customer[];
  searchCustomers: (query: string) => Customer[];
  getTopCustomers: (limit?: number) => Customer[];
  getCustomersByDateRange: (start: Date, end: Date) => Customer[];

  // ANALYTICS
  getTotalCustomers: () => number;
  getCustomerStats: () => {
    total: number;
    totalSpent: number;
    averageSpent: number;
    averageTransactions: number;
  };

  // UPDATE CUSTOMER TOTALS
  updateCustomerTotals: (customerId: string, amountDelta: number, countDelta: number) => void;

  // UTILITY
  setCustomers: (customers: Customer[]) => void;
  refreshCustomers: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* =================== CUSTOMER STORE =================== */
export const useCustomerStore = create<CustomerState>()(
  devtools(
    (set, get) => {
      // WIRE UP CROSS-STORE EVENT LISTENERS ONCE
      setupCustomerEventListeners(get);

      return {
        customers: [],
        isLoading: false,
        error: null,

        // CRUD OPERATIONS
        getCustomers: () => get().customers,

        getCustomerById: (id: string) => get().customers.find((c) => c.id === id),

        createCustomer: (customer) => {
          const newCustomer: Customer = {
            ...customer,
            id: `cust_${crypto.randomUUID() as string}`, // should be uuid
            joinedDate: new Date(),
            totalSpent: 0,
            transactionCount: 0,
          };
          set((state) => ({
            customers: [newCustomer, ...state.customers],
          }));

          // EMIT EVENT FOR NEW CUSTOMER
          storeEvents.emit(STORE_EVENTS.CUSTOMER_CREATED, newCustomer);

          return newCustomer;
        },

        updateCustomer: (id, updates) => {
          set((state) => ({
            customers: state.customers.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          }));

          // EMIT EVENT FOR UPDATED CUSTOMER
          const updatedCustomer = get().getCustomerById(id);
          if (updatedCustomer) {
            storeEvents.emit(STORE_EVENTS.CUSTOMER_UPDATED, updatedCustomer);
          }
        },

        deleteCustomer: (id) => {
          const deletedCustomer = get().getCustomerById(id);

          set((state) => ({
            customers: state.customers.filter((customer) => customer.id !== id),
          }));

          // EMIT EVENT FOR DELETED CUSTOMER
          if (deletedCustomer) {
            storeEvents.emit(STORE_EVENTS.CUSTOMER_DELETED, deletedCustomer);
          }
        },

        // FILTERING & SEARCHING
        filterCustomers: (filters) => {
          const { search, minSpent, maxSpent, startDate, endDate } = filters;

          // SINGLE-PASS FILTERING FOR BETTER PERFORMANCE
          return get().customers.filter((customer) => {
            // SEARCH FILTER
            if (search) {
              const query = search.toLowerCase();
              const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
              const matchesSearch =
                fullName.includes(query) ||
                customer.firstName.toLowerCase().includes(query) ||
                customer.lastName.toLowerCase().includes(query) ||
                customer.email?.toLowerCase().includes(query) ||
                customer.phone?.toLowerCase().includes(query);
              if (!matchesSearch) return false;
            }

            // SPENDING RANGE FILTERS
            if (minSpent !== undefined && customer.totalSpent < minSpent) return false;
            if (maxSpent !== undefined && customer.totalSpent > maxSpent) return false;

            // DATE RANGE FILTERS (skip if joinedDate is null)
            if (startDate && customer.joinedDate && customer.joinedDate < startDate) return false;
            if (endDate && customer.joinedDate && customer.joinedDate > endDate) return false;

            return true;
          });
        },

        searchCustomers: (query) => {
          const lowerQuery = query.toLowerCase();
          return get().customers.filter((c) => {
            const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
            return (
              fullName.includes(lowerQuery) ||
              c.firstName.toLowerCase().includes(lowerQuery) ||
              c.lastName.toLowerCase().includes(lowerQuery) ||
              c.email?.toLowerCase().includes(lowerQuery) ||
              c.phone?.toLowerCase().includes(lowerQuery) ||
              c.id.toLowerCase().includes(lowerQuery)
            );
          });
        },

        getTopCustomers: (limit = 10) =>
          [...get().customers].sort((a, b) => b.totalSpent - a.totalSpent).slice(0, limit),

        getCustomersByDateRange: (start, end) =>
          get().customers.filter(
            (c) => c.joinedDate && c.joinedDate >= start && c.joinedDate <= end
          ),

        // ANALYTICS
        getTotalCustomers: () => get().customers.length,

        getCustomerStats: () => {
          const { customers } = get();
          const totalSpent = customers.reduce((sum, c) => sum + c.totalSpent, 0);
          const totalTransactions = customers.reduce((sum, c) => sum + c.transactionCount, 0);

          return {
            total: customers.length,
            totalSpent,
            averageSpent: customers.length > 0 ? totalSpent / customers.length : 0,
            averageTransactions: customers.length > 0 ? totalTransactions / customers.length : 0,
          };
        },

        // INTERNAL - UPDATE CUSTOMER TOTALS
        updateCustomerTotals: (customerId, amountDelta, countDelta) => {
          const customer = get().getCustomerById(customerId);
          if (customer) {
            set((state) => ({
              customers: state.customers.map((c) =>
                c.id === customerId
                  ? {
                      ...c,
                      totalSpent: Math.max(0, c.totalSpent + amountDelta),
                      transactionCount: Math.max(0, c.transactionCount + countDelta),
                    }
                  : c
              ),
            }));
          }
        },

        // UTILITY
        setCustomers: (customers) => {
          set({ customers });
        },

        refreshCustomers: () => {
          set({ customers: [] });
        },

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error }),
      };
    },
    { name: 'customer-store' }
  )
);

/* ======================= EVENT LISTENERS SETUP ======================= */
function setupCustomerEventListeners(get: () => CustomerState) {
  // NEW COMPETED PAYMENT → ADD AMOUNT + INCREMENT AMOUNT
  storeEvents.on(STORE_EVENTS.TRANSACTION_CREATED, (data) => {
    const transaction = data as Transaction;

    if (transaction.customerId && isCompletedStatus(transaction.status)) {
      // AMOUNTS ARE ALREADY IN CENTS SAME AS customer.totalSpent
      get().updateCustomerTotals(transaction.customerId, transaction.amount, 1);
    }
  });

  // COMPLETED PAYMENT DELETED → SUBTRACT AMOUNT + DECREMENT AMOUNT
  storeEvents.on(STORE_EVENTS.TRANSACTION_DELETED, (data) => {
    const transaction = data as Transaction;

    if (transaction.customerId && isCompletedStatus(transaction.status)) {
      // AMOUNTS ARE ALREADY IN CENTS SAME AS customer.totalSpent
      get().updateCustomerTotals(transaction.customerId, -transaction.amount, -1);
    }
  });
}
